import { Component, Input, NgZone, OnInit, ViewChildren, QueryList, ElementRef } from "@angular/core";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { GameObject, GameType, GamesDataByLogin, RankingByLogin } from "./game.models";
import { GamesService } from "src/app/gamification/command-center/services/games.service";
import { ModuleManager } from "src/shared/models/module-manager";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { NavigationService } from 'src/app/navigation/navigation.service';
import OverlayScrollbars from 'overlayscrollbars';

@Component({
    selector: 'cxone-games',
    templateUrl: './cxone-games.component.html',
    styleUrl: './cxone-games.component.scss'
})
export class CxoneGamesComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChildren('cardDivOngoing') cardDivsOngoing!: QueryList<ElementRef>;
    @ViewChildren('cardDivEnded') cardDivsEnded!: QueryList<ElementRef>;
    private loadedIds: Set<number> = new Set<number>();

    private observerOngoing!: IntersectionObserver;
    private observerEnded!: IntersectionObserver;

    /** Error state trigger */
    public isDataLoading = false;
    private loginId: number = 0;

    public gamesData: GamesDataByLogin;
    public currentGames: GameObject[];
    public upcomingGames: GameObject[];
    public endedGames: GameObject[];
    public noData = false;
    public noPMLicense = false;
    public somethingWentWrong = false;
    public rankingData: RankingByLogin[] = [];
    public reloadingRanking = false;
    activeLoadings = 0;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private gamesService: GamesService,
        private navigationService: NavigationService,
        private zone: NgZone,
    ) { }

    ngOnInit() {
        this.moduleManager.showLoader = true;
        this.moduleManager.setOptions({
            onSettingsApplied: settings => {
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FiveMinute,

            onRefresh: (isAutoRefresh) => {
                this.reloadingRanking = true;
                this.loadedIds.clear();
                this.rankingData = [];
                this.zone.run(() => this.loadData(isAutoRefresh))
            },
            canRefresh: () => {
                return !this.moduleManager.showLoader;
            },
        });

        this.loadLocalization();
        this.noPMLicense = !this.navigationService.hasPartnerPMAccess;
    }

    ngAfterViewInit() {
        this.cardDivsOngoing.changes.subscribe(() => this.initializeIntersectionObserver());
        this.cardDivsEnded.changes.subscribe(() => this.initializeIntersectionObserver());
    }

    initializeIntersectionObserver() {
        const options: IntersectionObserverInit = {
            root: document.querySelector('#gamesDiv') as Element, 
            rootMargin: '0px',
            threshold: 0.1,
        };

        this.observerOngoing = new IntersectionObserver(
            this.handleIntersection.bind(this),
            options
        );

        this.observerEnded = new IntersectionObserver(
            this.handleIntersection.bind(this),
            options
        );

        this.cardDivsOngoing.forEach((div) => this.observerOngoing.observe(div.nativeElement));
        this.cardDivsEnded.forEach((div) => this.observerEnded.observe(div.nativeElement));
    }

    handleIntersection(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const id = parseInt(entry.target.id);
                if (!this.loadedIds.has(id)) {
                    this.loadRankingData(id);
                    this.loadedIds.add(id);
                }
            }
        });
    }

    scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "scrollbar-games"
        }
    };

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('Games_Module');
        }
        catch {
            console.error('Failed to load localization for Games_Module');
        }
    }

    private async loadData(isAutoRefresh: boolean) {
        if (!this.noPMLicense) {
            if (this.isDataLoading)
                return; // don't have multiple outgoing requests

            this.isDataLoading = true;
            this.moduleManager.showLoader = true;
            this.somethingWentWrong = false;

            try {
                this.gamesData = await this.gamesService.getGamesData(this.moduleKey, this.loginId);
                this.currentGames = this.gamesData?.currentGames;
                this.upcomingGames = this.gamesData?.openGames;
                this.endedGames = this.gamesData?.endedGames;
            } catch (err) {
                this.moduleManager.handleLoadError(err, isAutoRefresh);
                this.somethingWentWrong = true;
                return;
            }
            finally {
                this.moduleManager.showLoader = false;
                this.noData = this.gamesData === undefined 
                    || this.gamesData === null || 
                    (
                        this.upcomingGames.length === 0 &&
                        this.currentGames.length === 0 &&
                        this.endedGames.length === 0
                    ) || (typeof this.gamesData === 'object' && Object.keys(this.gamesData).length === 0);
                this.moduleManager.setAutoRefreshReady();
                this.isDataLoading = false;
                if (this.reloadingRanking) {
                    this.initializeIntersectionObserver();
                }
            }
        }
    }

    private async loadRankingData(gameId: number) {
        if (!this.noPMLicense) {
            this.activeLoadings++;
            this.moduleManager.showLoader = true;
            this.somethingWentWrong = false;

            try {
                this.rankingData.push(await this.gamesService.getGamesRanking(this.moduleKey, this.loginId, gameId));
            } catch (err) {
                this.moduleManager.handleLoadError(err);
                this.somethingWentWrong = true;
                return;
            }
            finally {
                this.activeLoadings--;
                if (this.activeLoadings === 0) {
                    this.moduleManager.showLoader = false;
                }
                this.noData = this.rankingData === undefined || this.rankingData === null;
                this.moduleManager.setAutoRefreshReady();
                this.isDataLoading = false;
            }
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Games_Module", key, defaultValue);
        return baseString;
    }

    public trackItem(index: number, item: any) {
        return item.underlyingRule.RuleID;
    }

    findObjectByRuleID(ruleID) {
        const item = this.rankingData?.find(item => item.gameRewards?.Rule?.RuleID === ruleID);
        if (item && item.gameRewards?.Rankings) {
            if (item.gameRewards?.Rule?.GameType == GameType.TopTeam)
                item.gameRewards?.Rankings?.sort((a, b) => a.GroupName.localeCompare(b.GroupName));
            else
                item.gameRewards?.Rankings?.sort((a, b) => a.Name.localeCompare(b.Name));
        }
        return item;
    };
}
